import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

export const ImageLightbox = ({ slides, index, onIndex, onClose }) => {
  const [zoom, setZoom] = useState(false);
  const touchX = useRef(null);
  const box = useRef(null);
  const n = slides.length;
  const go = (dir) => { setZoom(false); onIndex((index + dir + n) % n); };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  useEffect(() => {
    const el = box.current;
    if (zoom && el) requestAnimationFrame(() => { el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2; el.scrollTop = (el.scrollHeight - el.clientHeight) / 2; });
  }, [zoom]);

  const onTouchStart = (e) => { touchX.current = e.touches.length === 1 ? e.touches[0].clientX : null; };
  const onTouchEnd = (e) => {
    if (zoom || touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
  };

  const s = slides[index];
  return createPortal(
    <div className="fixed inset-0 z-[1100] bg-black flex flex-col animate-in fade-in duration-200" data-testid="tech-lightbox">
      <div className="flex items-center justify-between px-4 py-3 text-white/70">
        <span className="text-xs tracking-[0.2em]" style={{ fontFamily: "Space Grotesk" }} data-testid="tech-lightbox-counter">{index + 1} / {n}</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setZoom((z) => !z)} aria-label="Zoom" data-testid="tech-lightbox-zoom" className="p-2 rounded-full hover:bg-white/10 hover:text-white transition-colors">
            {zoom ? <ZoomOut size={22} /> : <ZoomIn size={22} />}
          </button>
          <button onClick={onClose} aria-label="Chiudi" data-testid="tech-lightbox-close" className="p-2 rounded-full hover:bg-white/10 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
      </div>
      <div ref={box} className={`relative flex-1 min-h-0 ${zoom ? "overflow-auto flex items-center" : "overflow-hidden flex items-center justify-center"}`} style={{ touchAction: zoom ? "pan-x pan-y pinch-zoom" : "pinch-zoom" }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <img
          src={s.image}
          alt={s.caption || `${index + 1}`}
          onClick={() => setZoom((z) => !z)}
          data-testid="tech-lightbox-img"
          className={zoom ? "max-w-none w-[220%] md:w-[160%] cursor-zoom-out" : "max-w-full max-h-full object-contain cursor-zoom-in"}
        />
      </div>
      {n > 1 && !zoom && (
        <>
          <button onClick={() => go(-1)} aria-label="Precedente" data-testid="tech-lightbox-prev" className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 bg-white/10 hover:bg-white/25 text-white rounded-full transition-colors">
            <ChevronLeft size={24} />
          </button>
          <button onClick={() => go(1)} aria-label="Successivo" data-testid="tech-lightbox-next" className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 bg-white/10 hover:bg-white/25 text-white rounded-full transition-colors">
            <ChevronRight size={24} />
          </button>
        </>
      )}
      {s.caption && (
        <p className="px-6 py-4 text-center text-white/80 text-sm md:text-base" style={{ fontFamily: "Inter" }} data-testid="tech-lightbox-caption">{s.caption}</p>
      )}
    </div>,
    document.body
  );
};
